import { CarrierBillingAccountType } from "@redotech/congo-enums/carrier-billing-account-type";
import { WeightUnit } from "@redotech/crdb-enums/product/weight-unit";
import { useRequiredContext } from "@redotech/react-util/context";
import { useHandler } from "@redotech/react-util/hook";
import { useLoad } from "@redotech/react-util/load";
import { TimeUnit } from "@redotech/redo-common-model/time";
import { omsActionStepSchema } from "@redotech/redo-flows-common/advanced-flow-db-parser";
import {
  AutoHoldType,
  DelayMarketplaceFulfillmentActionStep,
  GeneratePersonalizedSlipActionStep,
  OmsActionType,
  OmsSettableFields,
  SetDeliveryMethodActionStep,
  SetPackingSlipTemplateActionStep,
  SetParcelActionStep,
  SetShippingServiceActionStep,
  SetShippingServiceCheapestActionStep,
  SetShippingServiceCheapestWithinDeliveryDaysActionStep,
  SetShippingServiceFastestActionStep,
  SetThirdPartyBillingAccountActionStep,
  UpdateShopifyOrderActionStep,
  UpdateShopifyOrderField,
  WeightRoundingType,
} from "@redotech/redo-flows-common/oms/actions";
import { findStaleParcelReferences } from "@redotech/redo-flows-common/oms/stale-parcel-references";
import { SchemaType } from "@redotech/redo-flows-common/schemas/schemas";
import { OmsActionStep } from "@redotech/redo-flows-common/steps";
import { StepType } from "@redotech/redo-flows-core-schema-common/common";
import { Schema } from "@redotech/redo-flows-core-schema-common/type-system/schema";
import { billingAccountType } from "@redotech/redo-fulfillment-carrier-account-common/carrier-billing-accounts";
import {
  CarrierAndService,
  carrierToLabel,
} from "@redotech/redo-fulfillment-carrier-common/carriers-and-services";
import {
  RedoShopifyDeliveryMethodType,
  RedoShopifyDeliveryMethodTypeToString,
} from "@redotech/redo-fulfillment-group-common/fulfillment-delivery-method-type";
import {
  isCheapestWithinDaysSortingCriteria,
  SortingCriteria,
  type CheapestWithinDaysSortingCriteria,
} from "@redotech/redo-fulfillment-group-common/fulfillment-group";
import { RedoShopifyFulfillmentHoldReason } from "@redotech/redo-fulfillment-group-common/fulfillment-hold-reason";
import { PageSize } from "@redotech/redo-fulfillment-operations-common/packing-slips/packing-slip-builder";
import { PackingSlip } from "@redotech/redo-fulfillment-operations-common/packing-slips/packing-slip-definition";
import { PAGE_SIZE_LABELS } from "@redotech/redo-fulfillment-operations-common/packing-slips/page-size-labels";
import { PersonalizedSlip } from "@redotech/redo-fulfillment-operations-common/personalized-slip/personalized-slip-definition";
import { PageOrientation } from "@redotech/redo-fulfillment-outbound-labels-common/outbound-label-enums";
import { convertWeight } from "@redotech/redo-fulfillment-outbound-labels-common/util";
import { Parcel } from "@redotech/redo-fulfillment-parcel-common/parcel";
import {
  carrierAccountSettingsId,
  ICarrierAccount,
} from "@redotech/redo-fulfillment-rate-common/carrier-account";
import { t, useTranslation } from "@redotech/redo-i18n";
import XIcon from "@redotech/redo-icons/x-close.svg";
import { RedoMerchantRpcClientContext } from "@redotech/redo-merchant-app-common/rpc-client";
import { TeamContext } from "@redotech/redo-merchant-app-common/team";
import { CarrierAndServiceDropdown } from "@redotech/redo-merchant-fulfillment-app/common/carrier-and-service-dropdown";
import { getLabelText } from "@redotech/redo-merchant-fulfillment-app/common/shipment-rate-selection-dropdown";
import { OutboundRateOption } from "@redotech/redo-merchant-fulfillment-app/common/util";
import { RedoOutboundLabelsRpcClientContext } from "@redotech/redo-merchant-fulfillment-app/outbound-labels-rpc-client";
import { RedoAlert } from "@redotech/redo-web/arbiter-components/alert/redo-alert";
import { RedoCheckbox } from "@redotech/redo-web/arbiter-components/checkbox/redo-checkbox";
import { RedoIncrementDecrement } from "@redotech/redo-web/arbiter-components/increment-decrement/redo-increment-decrement";
import { RedoTextInput } from "@redotech/redo-web/arbiter-components/input/redo-text-input";
import {
  BadgeRedoListItem,
  RedoListItem,
  TextRedoListItem,
} from "@redotech/redo-web/arbiter-components/list/redo-list";
import { RedoMultiselectDropdown } from "@redotech/redo-web/arbiter-components/list/redo-multiselect-dropdown";
import { RedoSingleSelectDropdownInput } from "@redotech/redo-web/arbiter-components/select-dropdown/redo-single-select-dropdown-input";
import { RedoTagsDropdownInputButton } from "@redotech/redo-web/arbiter-components/select-dropdown/redo-tags-dropdown-input";
import { Flex } from "@redotech/redo-web/flex";
import {
  LogoSize,
  ShippingLogo,
} from "@redotech/redo-web/fulfillment/common/shipping-logo";
import * as inspectorStyles from "@redotech/redo-web/inspector.module.css";
import { Text } from "@redotech/redo-web/text";
import { filterTruthy } from "@redotech/util/array";
import { assertNever } from "@redotech/util/type";
import Fuse from "fuse.js";
import { memo, useMemo, useState } from "react";
import {
  FrontendStepType,
  PartialWithRequiredType,
  StepTypeDetailsProps,
} from "..";
import { ActionEditor, type ActionDefinition } from "../action-editor";
import { CustomerTagInput } from "../add-customer-tags";
import { OrderTagInput } from "../add-order-tags";
import { TagInput } from "../add-tags";
import { DelayMarketplaceFulfillmentInput } from "../delay-marketplace-fulfillment";
import { HoldDateInput, HoldDaysInput, HoldDetailsInput } from "../place-hold";
import { RoundWeightInput } from "../round-weight";
import { UserDropdownInput } from "../set-assigned-user";
import { BuyReturnLabelToggle } from "../set-buy-return-label";
import { DutiesPayerRadioGroup } from "../set-duties-payer";
import { FulfillmentServiceDropdown } from "../set-fulfillment-service";
import { OriginLocationDropdown } from "../set-origin-location";
import { OverrideUseOriginalDestinationAddressToggle } from "../set-override-use-original-destination-address";
import { ReturnAddressDropdown } from "../set-return-address";
import { SendLabelPurchasedNotificationToggle } from "../set-send-label-purchased-notification";
import { TaxIdentifiersDropdown } from "../set-tax-identifiers";
import { WeightInput } from "../set-weight";
import { ShippingOptionsInput } from "../shipping-options";
import { UpdateShopifyOrderInput } from "../update-shopify-order";
import { useCarrierAccountsForAutomation } from "./use-carrier-accounts";

type AutomationCarrierAccount = ReturnType<
  typeof useCarrierAccountsForAutomation
>["carrierAccounts"][number];

// "in" narrowing can't exclude the non-ICarrierAccount member (not a sealed type).
function isFullCarrierAccount(
  account: AutomationCarrierAccount,
): account is ICarrierAccount {
  return "provider" in account;
}

function carrierAccountOptionsFor(
  accounts: AutomationCarrierAccount[],
  translate: (key: string) => string,
): RedoListItem<string>[] {
  return accounts
    .filter((account) => !!account.carrierAccountId)
    .map((account) => {
      const settingsId = isFullCarrierAccount(account)
        ? carrierAccountSettingsId(account)
        : // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
          account.carrierAccountId!;
      return {
        id: settingsId,
        value: settingsId,
        type: "text" as const,
        text: account.name || translate("Merchant Account"),
      };
    });
}

const OmsShippingServiceInputs = memo(function OmsShippingServiceInputs({
  step,
  setStep,
}: {
  step: PartialWithRequiredType<SetShippingServiceActionStep>;
  setStep: (
    step: PartialWithRequiredType<SetShippingServiceActionStep>,
  ) => void;
}) {
  const { t } = useTranslation();
  const [showAdvancedOptions, setShowAdvancedOptions] = useState(
    !!step.carrierAccountId,
  );

  const setShippingService = useHandler((option: CarrierAndService) => {
    setStep({
      ...step,
      newShippingService: option.service,
      newShippingCarrier: option.carrier,
    });
  });

  const client = useRequiredContext(RedoOutboundLabelsRpcClientContext);
  const { carrierAccounts, carrierAccountsLoading } =
    useCarrierAccountsForAutomation();

  const carrierAccountOptions: RedoListItem<string>[] = useMemo(
    () => carrierAccountOptionsFor(carrierAccounts, t),
    [carrierAccounts, t],
  );

  const options = useLoad(async (signal: AbortSignal) => {
    const { options } = await client.getCarrierServiceCombinations(
      { includeDisabled: false, includeMerchantCarriers: true },
      { signal },
    );
    return options;
  }, []);

  const setCarrierAccount = useHandler(
    ({ item }: { item: RedoListItem<string> }) => {
      setStep({ ...step, carrierAccountId: item.value });
    },
  );

  const hasCarrierAccounts = carrierAccounts.length > 0;

  const { checkboxDisabled, checkboxTooltip } = useMemo(() => {
    if (carrierAccountsLoading) {
      return {
        checkboxDisabled: true,
        checkboxTooltip: t("Loading carrier accounts..."),
      };
    }
    if (!hasCarrierAccounts) {
      return {
        checkboxDisabled: true,
        checkboxTooltip: t("No merchant carrier accounts available"),
      };
    }
    return { checkboxDisabled: false, checkboxTooltip: undefined };
  }, [carrierAccountsLoading, hasCarrierAccounts, t]);

  return (
    <Flex dir="column" gap="md">
      <CarrierAndServiceDropdown
        itemsLoading={options.pending}
        onSelectionChange={setShippingService}
        options={options.value ?? []}
        selectedOption={
          step.newShippingCarrier && step.newShippingService
            ? {
                carrier: step.newShippingCarrier,
                service: step.newShippingService,
              }
            : null
        }
      />
      <Flex dir="column" gap="xs" py="xs">
        <RedoCheckbox
          disabled={checkboxDisabled}
          label={t("Advanced carrier mapping")}
          setValue={(value) => {
            setShowAdvancedOptions(value === true);
            if (value !== true) {
              setStep({ ...step, carrierAccountId: null });
            }
          }}
          size="sm"
          value={showAdvancedOptions}
        />
        {checkboxTooltip && (
          <Text fontSize="xs" textColor="secondary">
            {checkboxTooltip}
          </Text>
        )}
        {showAdvancedOptions && hasCarrierAccounts && (
          <Flex dir="column" gap="xs">
            <RedoSingleSelectDropdownInput<string>
              fitToAnchor
              flexGrow
              options={carrierAccountOptions}
              optionSelected={setCarrierAccount}
              placeholder={t("Select account")}
              selectedItem={carrierAccountOptions.find(
                (opt) => opt.value === step.carrierAccountId,
              )}
              size="xs"
            />
          </Flex>
        )}
      </Flex>
    </Flex>
  );
});

function deliveryWindowOptions(): TextRedoListItem<CheapestWithinDaysSortingCriteria>[] {
  return [
    {
      id: SortingCriteria.CHEAPEST_WITHIN_DELIVERY_DAYS,
      supportingText: t("Counts Monday through Saturday"),
      text: t("Delivery days"),
      type: "text",
      value: SortingCriteria.CHEAPEST_WITHIN_DELIVERY_DAYS,
    },
    {
      id: SortingCriteria.CHEAPEST_WITHIN_BUSINESS_DAYS,
      supportingText: t("Counts weekdays and excludes U.S. holidays"),
      text: t("Business days"),
      type: "text",
      value: SortingCriteria.CHEAPEST_WITHIN_BUSINESS_DAYS,
    },
    {
      id: SortingCriteria.CHEAPEST_WITHIN_DELIVERABLE_DAYS,
      supportingText: t(
        "Counts only days when the selected carrier and service can deliver",
      ),
      text: t("Deliverable days"),
      type: "text",
      value: SortingCriteria.CHEAPEST_WITHIN_DELIVERABLE_DAYS,
    },
  ];
}

function getDeliveryWindowSortingCriteria(
  step: PartialWithRequiredType<OmsActionStep>,
): CheapestWithinDaysSortingCriteria {
  if (
    "selectionCriteria" in step &&
    isCheapestWithinDaysSortingCriteria(step.selectionCriteria)
  ) {
    return step.selectionCriteria;
  }
  return SortingCriteria.CHEAPEST_WITHIN_DELIVERY_DAYS;
}

const OmsShippingServiceGroupInputs = memo(function OmsShippingServiceInputs({
  step,
  setStep,
  selectionCriteria,
}: {
  step:
    | PartialWithRequiredType<SetShippingServiceCheapestActionStep>
    | PartialWithRequiredType<SetShippingServiceFastestActionStep>
    | PartialWithRequiredType<SetShippingServiceCheapestWithinDeliveryDaysActionStep>;
  setStep: (
    step:
      | PartialWithRequiredType<SetShippingServiceCheapestActionStep>
      | PartialWithRequiredType<SetShippingServiceFastestActionStep>
      | PartialWithRequiredType<SetShippingServiceCheapestWithinDeliveryDaysActionStep>,
  ) => void;
  selectionCriteria: SortingCriteria;
}) {
  const { t } = useTranslation();
  const client = useRequiredContext(RedoOutboundLabelsRpcClientContext);
  const { carrierAccounts, carrierAccountsLoading } =
    useCarrierAccountsForAutomation();

  const hasAnyCarrierAccount = useMemo(() => {
    return (
      step.servicesToChooseFrom?.some((s) => s.carrierAccountId) ||
      step.servicesToPrioritize?.some((s) => s.carrierAccountId)
    );
  }, [step.servicesToChooseFrom, step.servicesToPrioritize]);

  const [showAdvancedOptions, setShowAdvancedOptions] =
    useState(hasAnyCarrierAccount);

  const hasCarrierAccounts = carrierAccounts.length > 0;
  const usesDeliveryWindow =
    isCheapestWithinDaysSortingCriteria(selectionCriteria);
  const selectedDeliveryWindowOption = deliveryWindowOptions().find(
    (option) => option.value === selectionCriteria,
  );

  const setDeliveryWindow = useHandler(
    ({ item }: { item: RedoListItem<CheapestWithinDaysSortingCriteria> }) => {
      setStep({
        ...step,
        selectionCriteria: item.value,
      } as PartialWithRequiredType<SetShippingServiceCheapestWithinDeliveryDaysActionStep>);
    },
  );

  const { checkboxDisabled, checkboxTooltip } = useMemo(() => {
    if (carrierAccountsLoading) {
      return {
        checkboxDisabled: true,
        checkboxTooltip: t("Loading carrier accounts..."),
      };
    }
    if (!hasCarrierAccounts) {
      return {
        checkboxDisabled: true,
        checkboxTooltip: t("No merchant carrier accounts available"),
      };
    }
    return { checkboxDisabled: false, checkboxTooltip: undefined };
  }, [carrierAccountsLoading, hasCarrierAccounts, t]);

  const carrierAndServiceOptionLoad = useLoad(async (signal: AbortSignal) => {
    const { options } = await client.getCarrierServiceCombinations(
      { includeDisabled: false, includeMerchantCarriers: true },
      { signal },
    );
    return options;
  }, []);

  // I swear I didn't want to do this but lint made me
  // The 'carrierAndServiceOptions' logical expression could make the dependencies of useMemo Hook (at line 391) change on every render. To fix this, wrap the initialization of 'carrierAndServiceOptions' in its own useMemo() Hook.
  const carrierAndServiceOptions = useMemo(() => {
    return carrierAndServiceOptionLoad.value ?? [];
  }, [carrierAndServiceOptionLoad.value]);

  // Handler for base services selection
  const setShippingServices = (options: OutboundRateOption[]) => {
    setStep({
      ...step,
      selectionCriteria,
      servicesToChooseFrom: options.map((option) => {
        // Preserve existing carrierAccountId if the service already exists
        const existing = step.servicesToChooseFrom?.find(
          (s) => s.carrier === option.carrier && s.service === option.service,
        );
        return {
          carrier: option.carrier,
          service: option.service,
          carrierAccountId: existing?.carrierAccountId ?? null,
        };
      }),
    } as PartialWithRequiredType<
      SetShippingServiceCheapestActionStep | SetShippingServiceFastestActionStep
    >);
  };

  // Handler for priority services selection
  const setPriorityServices = (options: OutboundRateOption[]) => {
    setStep({
      ...step,
      selectionCriteria,
      servicesToPrioritize: options.map((option) => {
        // Preserve existing carrierAccountId if the service already exists
        const existing = step.servicesToPrioritize?.find(
          (s) => s.carrier === option.carrier && s.service === option.service,
        );
        return {
          carrier: option.carrier,
          service: option.service,
          carrierAccountId: existing?.carrierAccountId ?? null,
        };
      }),
    } as PartialWithRequiredType<
      SetShippingServiceCheapestActionStep | SetShippingServiceFastestActionStep
    >);
  };

  const removeShippingService = useHandler(
    (option: BadgeRedoListItem<OutboundRateOption>) => {
      const oldServices = step.servicesToChooseFrom || [];
      const newServices = oldServices.filter(
        (service) =>
          !!service &&
          !!service.carrier &&
          !!service.service &&
          (service?.carrier !== option.value.carrier ||
            service?.service !== option.value.service),
      );
      setShippingServices(newServices as OutboundRateOption[]);
    },
  );

  const removePriorityService = useHandler(
    (option: BadgeRedoListItem<OutboundRateOption>) => {
      const oldServices = step.servicesToPrioritize || [];
      const newServices = oldServices.filter(
        (service) =>
          !!service &&
          !!service.carrier &&
          !!service.service &&
          (service?.carrier !== option.value.carrier ||
            service?.service !== option.value.service),
      );
      setPriorityServices(newServices as OutboundRateOption[]);
    },
  );

  const onDropdownValueChange = useHandler(
    (values: RedoListItem<OutboundRateOption>[]) => {
      setShippingServices(values.map((value) => value.value));
    },
  );

  const onPriorityDropdownValueChange = useHandler(
    (values: RedoListItem<OutboundRateOption>[]) => {
      setPriorityServices(values.map((value) => value.value));
    },
  );

  // State for base services dropdown
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [dropdownButtonRef, setDropdownButtonRef] =
    useState<HTMLButtonElement | null>(null);
  const [searchString, setSearchString] = useState("");

  const handleSetDropdownOpen = useHandler((open: boolean) => {
    setDropdownOpen(open);
    if (!open) {
      setSearchString("");
    }
  });

  // State for priority services dropdown
  const [priorityDropdownOpen, setPriorityDropdownOpen] = useState(false);
  const [priorityDropdownButtonRef, setPriorityDropdownButtonRef] =
    useState<HTMLButtonElement | null>(null);

  const baseSelectedOptions: CarrierAndService[] = useMemo(() => {
    if (!step.servicesToChooseFrom) {
      return [];
    }

    return filterTruthy(
      step.servicesToChooseFrom.map((option) => {
        if (!option || !option.carrier || !option.service) {
          return undefined;
        }
        return option as CarrierAndService;
      }),
    );
  }, [step.servicesToChooseFrom]);

  const options: RedoListItem<CarrierAndService>[] = useMemo(() => {
    return carrierAndServiceOptions.map((option) => {
      return {
        id: `${option.carrier}-${option.service}`,
        value: option,
        type: "text",
        text: getLabelText(option),
      };
    });
  }, [carrierAndServiceOptions]);

  const searcher = useMemo(
    () => new Fuse(options, { keys: ["text"], threshold: 0.3 }),
    [options],
  );

  const filteredOptions = useMemo(() => {
    if (!searchString.trim()) {
      return options;
    }
    return searcher.search(searchString).map((result) => result.item);
  }, [options, searchString, searcher]);

  const priorityOptions: RedoListItem<CarrierAndService>[] = useMemo(() => {
    // Only show services that have been selected in the base services dropdown
    return baseSelectedOptions.map((option) => {
      return {
        id: `${option.carrier}-${option.service}`,
        value: option,
        type: "text",
        text: getLabelText(option),
      };
    });
  }, [baseSelectedOptions]);

  const prioritySelectedOptions: CarrierAndService[] = useMemo(() => {
    if (!step.servicesToPrioritize) {
      return [];
    }

    return filterTruthy(
      step.servicesToPrioritize.map((option) => {
        if (!option || !option.carrier || !option.service) {
          return undefined;
        }
        return option as CarrierAndService;
      }),
    );
  }, [step.servicesToPrioritize]);

  const selectedButtonOptions: BadgeRedoListItem<CarrierAndService>[] =
    useMemo(() => {
      return baseSelectedOptions.map((option) => {
        return {
          id: `${option.carrier}-${option.service}`,
          value: option,
          type: "badge",
          badge: {
            text: getLabelText({
              carrier: option.carrier,
              service: option.service,
            }),
            segmentLeading: {
              Icon: () => (
                <ShippingLogo carrier={option.carrier} size={LogoSize.MEDIUM} />
              ),
              type: "icon",
            },
          },
        };
      });
    }, [baseSelectedOptions]);

  const priorityButtonOptions: BadgeRedoListItem<CarrierAndService>[] =
    useMemo(() => {
      return prioritySelectedOptions.map((option) => {
        return {
          id: `${option.carrier}-${option.service}`,
          value: option,
          type: "badge",
          badge: {
            text: getLabelText({
              carrier: option.carrier,
              service: option.service,
            }),
            segmentLeading: {
              Icon: () => (
                <ShippingLogo carrier={option.carrier} size={LogoSize.MEDIUM} />
              ),
              type: "icon",
            },
          },
        };
      });
    }, [prioritySelectedOptions]);

  const selectedDropdownOptions: RedoListItem<CarrierAndService>[] =
    useMemo(() => {
      return baseSelectedOptions.map((option) => {
        return {
          id: `${option.carrier}-${option.service}`,
          value: option,
          type: "text",
          text: getLabelText(option),
        };
      });
    }, [baseSelectedOptions]);

  const priorityDropdownOptions: RedoListItem<CarrierAndService>[] =
    useMemo(() => {
      return prioritySelectedOptions.map((option) => {
        return {
          id: `${option.carrier}-${option.service}`,
          value: option,
          type: "text",
          text: getLabelText(option),
        };
      });
    }, [prioritySelectedOptions]);

  const onSelectAll = useHandler(() => {
    if (step.servicesToChooseFrom?.length !== carrierAndServiceOptions.length) {
      setShippingServices(carrierAndServiceOptions);
    } else {
      setShippingServices([]);
    }
  });

  const selectAllState = useMemo(() => {
    if (step.servicesToChooseFrom?.length === carrierAndServiceOptions.length) {
      return true;
    }
    return false;
  }, [step.servicesToChooseFrom, carrierAndServiceOptions]);

  const setCarrierAccountForService = useHandler(
    (carrier: string, service: string, carrierAccountId: string) => {
      const updatedServicesToChooseFrom = step.servicesToChooseFrom?.map(
        (s) => {
          if (s.carrier === carrier && s.service === service) {
            return { ...s, carrierAccountId };
          }
          return s;
        },
      );

      const updatedServicesToPrioritize = step.servicesToPrioritize?.map(
        (s) => {
          if (s.carrier === carrier && s.service === service) {
            return { ...s, carrierAccountId };
          }
          return s;
        },
      );

      setStep({
        ...step,
        servicesToChooseFrom: updatedServicesToChooseFrom,
        servicesToPrioritize: updatedServicesToPrioritize,
      } as PartialWithRequiredType<
        | SetShippingServiceCheapestActionStep
        | SetShippingServiceFastestActionStep
        | SetShippingServiceCheapestWithinDeliveryDaysActionStep
      >);
    },
  );

  const carrierAccountOptions: RedoListItem<string>[] = useMemo(
    () => carrierAccountOptionsFor(carrierAccounts, t),
    [carrierAccounts, t],
  );

  return (
    <Flex dir="column" gap="md">
      <div
        // design-allow: existing scroll container, unchanged by this conflict
        style={{ maxHeight: "20rem", overflow: "auto", position: "relative" }}
      >
        <RedoTagsDropdownInputButton
          isDropdownOpen={dropdownOpen}
          onClick={() => handleSetDropdownOpen(!dropdownOpen)}
          placeholder={t("Select services")}
          ref={setDropdownButtonRef}
          removeItem={removeShippingService}
          selectedItems={selectedButtonOptions}
          size="xs"
        />
      </div>

      {usesDeliveryWindow && (
        <>
          <Text fontSize="xs" fontWeight="medium">
            {t("Deliver within")}
          </Text>
          <RedoIncrementDecrement
            min={1}
            setValue={(value) => {
              setStep({
                ...step,
                selectionCriteria,
                targetDeliveryDays: value,
              } as PartialWithRequiredType<SetShippingServiceCheapestWithinDeliveryDaysActionStep>);
            }}
            size="small"
            step={1}
            suffix={t("days")}
            value={
              "targetDeliveryDays" in step ? (step.targetDeliveryDays ?? 1) : 1
            }
          />
          <RedoSingleSelectDropdownInput<CheapestWithinDaysSortingCriteria>
            label={t("Count days as")}
            options={deliveryWindowOptions()}
            optionSelected={setDeliveryWindow}
            selectedItem={selectedDeliveryWindowOption}
            size="xs"
          />
        </>
      )}
      <RedoMultiselectDropdown<CarrierAndService>
        dropdownAnchor={dropdownButtonRef}
        dropdownOpen={dropdownOpen}
        dropdownPlacement="left-start"
        fitToAnchor
        items={filteredOptions}
        searchPlaceholder={t("Search carriers and services...")}
        searchString={searchString}
        selectAllState={selectAllState}
        selectAllToggled={onSelectAll}
        selectedItems={selectedDropdownOptions}
        selectionVariant="checkbox"
        setDropdownOpen={handleSetDropdownOpen}
        setSearchString={setSearchString}
        setSelectedItems={onDropdownValueChange}
        size="xs"
      />
      {!usesDeliveryWindow && (
        <>
          <Text fontSize="xs" fontWeight="medium">
            {t("Prioritize services (optional)")}
          </Text>
          <Flex align="flex-start">
            <RedoTagsDropdownInputButton
              isDropdownOpen={priorityDropdownOpen}
              onClick={() => setPriorityDropdownOpen(!priorityDropdownOpen)}
              placeholder={t("Select priority services")}
              ref={setPriorityDropdownButtonRef}
              removeItem={removePriorityService}
              selectedItems={priorityButtonOptions}
              size="xs"
            />
            <RedoMultiselectDropdown<CarrierAndService>
              dropdownAnchor={priorityDropdownButtonRef}
              dropdownOpen={priorityDropdownOpen}
              dropdownPlacement="left-start"
              fitToAnchor
              items={priorityOptions}
              selectedItems={priorityDropdownOptions}
              selectionVariant="checkbox"
              setDropdownOpen={setPriorityDropdownOpen}
              setSelectedItems={onPriorityDropdownValueChange}
              size="xs"
            />
          </Flex>
        </>
      )}
      {!usesDeliveryWindow && (
        <>
          <Text fontSize="xs" fontWeight="medium">
            {t("if within")}
          </Text>
          <RedoIncrementDecrement
            min={0}
            prefix="$"
            setValue={(value) => {
              setStep({
                ...step,
                selectionCriteria,
                maxPriorityPriceDifference: value,
              } as PartialWithRequiredType<
                | SetShippingServiceCheapestActionStep
                | SetShippingServiceFastestActionStep
              >);
            }}
            size="small"
            step={0.01}
            value={step.maxPriorityPriceDifference ?? 0}
          />
        </>
      )}
      <Flex dir="column" gap="xs" py="xs">
        <RedoCheckbox
          disabled={checkboxDisabled}
          label={t("Advanced carrier mapping")}
          setValue={(value) => {
            setShowAdvancedOptions(value === true);
            if (value !== true) {
              // Clear all carrier account IDs when disabling advanced options
              const clearedServicesToChooseFrom =
                step.servicesToChooseFrom?.map((s) => ({
                  ...s,
                  carrierAccountId: null,
                }));
              const clearedServicesToPrioritize =
                step.servicesToPrioritize?.map((s) => ({
                  ...s,
                  carrierAccountId: null,
                }));
              setStep({
                ...step,
                servicesToChooseFrom: clearedServicesToChooseFrom,
                servicesToPrioritize: clearedServicesToPrioritize,
              } as PartialWithRequiredType<
                | SetShippingServiceCheapestActionStep
                | SetShippingServiceFastestActionStep
                | SetShippingServiceCheapestWithinDeliveryDaysActionStep
              >);
            }
          }}
          size="sm"
          value={showAdvancedOptions ?? false}
        />
        {checkboxTooltip && (
          <Text fontSize="xs" textColor="secondary">
            {checkboxTooltip}
          </Text>
        )}
        {showAdvancedOptions &&
          hasCarrierAccounts &&
          step.servicesToChooseFrom && (
            <Flex dir="column" gap="sm">
              <Text fontSize="xs" fontWeight="medium">
                {t("Carrier Accounts")}
              </Text>
              {step.servicesToChooseFrom
                .filter((service) => service.carrier && service.service)
                .map((service) => {
                  if (!service.carrier || !service.service) {
                    return null;
                  }
                  return (
                    <Flex
                      align="center"
                      dir="row"
                      gap="md"
                      key={`${service.carrier}-${service.service}`}
                    >
                      <Flex flex={1}>
                        <Text fontSize="xs">
                          {getLabelText({
                            carrier: service.carrier,
                            service: service.service,
                          })}
                        </Text>
                      </Flex>
                      <Flex flex={1}>
                        <RedoSingleSelectDropdownInput<string>
                          fitToAnchor={false}
                          flexGrow
                          options={carrierAccountOptions}
                          optionSelected={({ item }) => {
                            setCarrierAccountForService(
                              // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
                              service.carrier!,
                              // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
                              service.service!,
                              item.value,
                            );
                          }}
                          placeholder={t("Select account")}
                          selectedItem={carrierAccountOptions.find(
                            (opt) => opt.value === service.carrierAccountId,
                          )}
                          size="xs"
                        />
                      </Flex>
                    </Flex>
                  );
                })}
            </Flex>
          )}
      </Flex>
    </Flex>
  );
});

const ParcelInput = memo(function ParcelInput({
  step,
  setStep,
}: {
  step: PartialWithRequiredType<SetParcelActionStep>;
  setStep: (step: PartialWithRequiredType<SetParcelActionStep>) => void;
}) {
  const { t } = useTranslation();
  const outboundLabelRpcClient = useRequiredContext(
    RedoOutboundLabelsRpcClientContext,
  );
  const parcelsLoad = useLoad(async () => {
    const result = await outboundLabelRpcClient.getParcels(null);
    return result.parcels;
  }, []);

  const parcelOptions: RedoListItem<Parcel>[] = useMemo(() => {
    if (!parcelsLoad.value) {
      return [];
    }

    return parcelsLoad.value.map((parcel) => ({
      value: parcel,
      id: parcel.name,
      type: "text",
      text: parcel.name,
    }));
  }, [parcelsLoad.value]);

  const virtualize = parcelOptions.length > 100;

  const { hasExistingOverrides, currentParcelNames } = useMemo(() => {
    // Mongoose returns an empty array if no parcels are set which is bad
    const currentParcelNames = step.parcels?.length
      ? step.parcels
      : step.parcel
        ? [step.parcel]
        : [];
    const hasExistingOverrides = currentParcelNames.some(
      (p) => !!p?.parcelAndContentWeight,
    );
    return { hasExistingOverrides, currentParcelNames };
  }, [step.parcels, step.parcel]);

  const [showWeightOverrides, setShowWeightOverrides] =
    useState(hasExistingOverrides);

  const updateParcel = useHandler((index: number, parcel: Parcel) => {
    const newParcels = [...currentParcelNames];
    newParcels[index] = {
      ...parcel,
      parcelAndContentWeight: newParcels[index]?.parcelAndContentWeight,
    };
    setStep({ ...step, parcels: newParcels, parcel: newParcels[0] });
  });

  const removeParcel = useHandler((index: number) => {
    const newParcels = currentParcelNames.filter((_, i) => i !== index);
    setStep({ ...step, parcels: newParcels, parcel: newParcels[0] });
  });

  const addParcel = useHandler(() => {
    const firstParcel = parcelsLoad.value?.[0];
    if (firstParcel) {
      const newParcels = [...currentParcelNames, firstParcel];
      setStep({ ...step, parcels: newParcels, parcel: newParcels[0] });
    }
  });

  const toggleWeightOverrides = useHandler((enabled: boolean) => {
    setShowWeightOverrides(enabled);
    if (!enabled) {
      const newParcels = currentParcelNames.map((p) => ({
        ...p,
        parcelAndContentWeight: undefined,
      }));
      setStep({ ...step, parcels: newParcels });
    }
  });

  const updateOverrideWeightPounds = useHandler(
    (index: number, pounds: number) => {
      if (!showWeightOverrides) {
        return;
      }
      const currentWeight = currentParcelNames[index]
        ?.parcelAndContentWeight ?? { value: 0, unit: WeightUnit.Oz };
      const currentOunces =
        currentWeight?.unit === WeightUnit.Oz
          ? (currentWeight.value ?? 0) % 16
          : 0;
      const totalOunces = pounds * 16 + currentOunces;
      const newParcels = [...currentParcelNames];
      newParcels[index] = {
        ...newParcels[index],
        parcelAndContentWeight: { value: totalOunces, unit: WeightUnit.Oz },
      };
      setStep({ ...step, parcels: newParcels });
    },
  );

  const updateOverrideWeightOunces = useHandler(
    (index: number, ounces: number) => {
      if (!showWeightOverrides) {
        return;
      }
      const currentWeight = currentParcelNames[index]?.parcelAndContentWeight;
      const currentPounds =
        currentWeight?.unit === WeightUnit.Oz
          ? Math.floor((currentWeight.value ?? 0) / 16)
          : 0;
      const totalOunces = currentPounds * 16 + ounces;
      const newParcels = [...currentParcelNames];
      newParcels[index] = {
        ...newParcels[index],
        parcelAndContentWeight: { value: totalOunces, unit: WeightUnit.Oz },
      };
      setStep({ ...step, parcels: newParcels });
    },
  );

  const hasMultipleParcels = currentParcelNames.length > 1;

  // A parcel deleted and re-added keeps its name but gets a new id, so the
  // dropdown still resolves by name while the step dispatches on a dead id.
  const staleParcelIds = useMemo(() => {
    if (!parcelsLoad.value) {
      return new Set<string>();
    }
    const references = findStaleParcelReferences({
      steps: [
        {
          id: step.id ?? "",
          type: StepType.OMS_ACTION,
          actionType: OmsActionType.SET_PARCEL,
          parcels: currentParcelNames,
          parcel: step.parcel,
        },
      ],
      liveParcelIds: filterTruthy(
        parcelsLoad.value.map((parcel) => parcel._id?.toString()),
      ),
    });
    return new Set(references.map((reference) => reference.parcelId));
  }, [parcelsLoad.value, currentParcelNames, step.parcel, step.id]);

  return (
    <Flex dir="column" gap="sm" w="full">
      {staleParcelIds.size > 0 && (
        <RedoAlert
          color="warning"
          description={t(
            "Re-select them below so this step points at the current packages.",
          )}
          fullWidth
          size="sm"
          title={
            staleParcelIds.size === 1
              ? t("This step references a package that no longer exists.")
              : t(
                  "This step references {{count}} packages that no longer exist.",
                  { count: staleParcelIds.size },
                )
          }
        />
      )}
      {hasMultipleParcels && (
        <Flex align="center" gap="xs">
          <input // design-allow: native checkbox, unchanged by this conflict
            checked={showWeightOverrides}
            onChange={(e) => toggleWeightOverrides(e.target.checked)}
            type="checkbox"
          />
          <Text fontSize="xs" fontWeight="medium">
            {t("Override weights")}
          </Text>
        </Flex>
      )}
      {currentParcelNames.map((stepParcel, index) => {
        const parcel = parcelsLoad.value?.find(
          (p) => p.name === stepParcel?.name,
        );
        const stepParcelId = stepParcel?._id?.toString();
        const isStaleReference =
          !!stepParcelId && staleParcelIds.has(stepParcelId);
        const overrideWeight = stepParcel?.parcelAndContentWeight ?? {
          value: 0,
          unit: WeightUnit.Oz,
        };

        let pounds = 0;
        let ounces = 0;

        const convertedWeight = convertWeight(
          {
            value: overrideWeight.value ?? 0,
            unit: overrideWeight.unit ?? WeightUnit.Oz,
          },
          WeightUnit.Oz,
        );
        pounds = Math.floor(convertedWeight.value / 16);
        ounces = convertedWeight.value % 16;

        return (
          <Flex dir="column" gap="xs" key={index}>
            <Flex align="center" gap="sm">
              <RedoSingleSelectDropdownInput<Parcel>
                flexGrow
                options={parcelOptions}
                optionSelected={({ item }) => updateParcel(index, item.value)}
                placeholder={t("Select parcel")}
                selectedItem={
                  parcel
                    ? {
                        value: parcel,
                        id: parcel.name,
                        type: "text",
                        text: parcel.name,
                      }
                    : undefined
                }
                size="xs"
                virtualize={virtualize}
              />
              {currentParcelNames.length > 1 && (
                <div // design-allow: existing icon remove control, unchanged by this conflict
                  className={inspectorStyles.closeButton}
                  onClick={() => removeParcel(index)}
                >
                  <XIcon height="16" width="16" />
                </div>
              )}
            </Flex>
            {isStaleReference && (
              <Text fontSize="xs" textColor="warning">
                {stepParcel?.name
                  ? t(
                      '"{{parcelName}}" no longer exists — re-select it to link the current package.',
                      { parcelName: stepParcel.name },
                    )
                  : t(
                      "This package no longer exists — re-select it to link the current package.",
                    )}
              </Text>
            )}
            {hasMultipleParcels && showWeightOverrides && (
              <Flex dir="row" gap="md">
                <Flex flex={1}>
                  <RedoIncrementDecrement
                    fullWidth
                    min={0}
                    placeholder="0"
                    selectOnFocus
                    setValue={(value) =>
                      updateOverrideWeightPounds(index, value)
                    }
                    size="small"
                    step="any"
                    suffix={t("lbs")}
                    value={pounds}
                  />
                </Flex>
                <Flex flex={1}>
                  <RedoIncrementDecrement
                    fullWidth
                    min={0}
                    placeholder="0"
                    selectOnFocus
                    setValue={(value) =>
                      updateOverrideWeightOunces(index, value)
                    }
                    size="small"
                    step="any"
                    suffix={t("oz")}
                    value={ounces}
                  />
                </Flex>
              </Flex>
            )}
          </Flex>
        );
      })}
      <Text
        cursor="pointer"
        fontSize="xs"
        fontWeight="medium"
        onClick={addParcel}
      >
        {t("+ Add parcel")}
      </Text>
      {currentParcelNames.length > 0 && (
        <Flex mt="lg">
          <RedoCheckbox
            label={t("Add to existing parcels")}
            setValue={(value) =>
              setStep({ ...step, addToExistingParcels: value === true })
            }
            size="sm"
            value={!!step.addToExistingParcels}
          />
        </Flex>
      )}
    </Flex>
  );
});

const PackingSlipTemplateInput = memo(function PackingSlipTemplateInput({
  step,
  setStep,
}: {
  step: PartialWithRequiredType<SetPackingSlipTemplateActionStep>;
  setStep: (
    step: PartialWithRequiredType<SetPackingSlipTemplateActionStep>,
  ) => void;
}) {
  const { t } = useTranslation();
  const team = useRequiredContext(TeamContext);
  const merchantRpcClient = useRequiredContext(RedoMerchantRpcClientContext);

  const packingSlipTemplates = useLoad(async (signal) => {
    try {
      const result = await merchantRpcClient.getPackingSlipTemplatesByTeam(
        { teamId: team._id },
        { signal },
      );
      return result || [];
    } catch (error) {
      console.error("Failed to load packing slip templates:", error);
      return [];
    }
  }, []);

  const templateOptions: RedoListItem<PackingSlip>[] = useMemo(() => {
    if (
      !packingSlipTemplates.value ||
      packingSlipTemplates.value.length === 0
    ) {
      return [];
    }

    return packingSlipTemplates.value.map((template) => ({
      id: template._id,
      value: template,
      type: "text" as const,
      text: template.name,
    }));
  }, [packingSlipTemplates.value]);

  const setPackingSlipTemplate = useHandler(
    ({ item }: { item: RedoListItem<PackingSlip> }) => {
      setStep({
        ...step,
        packingSlipTemplateId: item.value._id,
        packingSlipName: item.value.name,
      });
    },
  );

  return (
    <RedoSingleSelectDropdownInput
      fitToAnchor={false}
      flexGrow={false}
      options={templateOptions}
      optionSelected={setPackingSlipTemplate}
      placeholder={t("Select template")}
      selectedItem={templateOptions.find(
        (template) => template.value._id === step.packingSlipTemplateId,
      )}
      size="xs"
    />
  );
});

function personalizedSlipOrientationLabel(orientation: PageOrientation) {
  return {
    [PageOrientation.PORTRAIT]: t("Portrait"),
    [PageOrientation.LANDSCAPE]: t("Landscape"),
  }[orientation];
}

const TEMPLATE_DEFAULT_OPTION_ID = "__template_default__";

const PersonalizedInsertTemplateInput = memo(
  function PersonalizedInsertTemplateInput({
    step,
    setStep,
  }: {
    step: PartialWithRequiredType<GeneratePersonalizedSlipActionStep>;
    setStep: (
      step: PartialWithRequiredType<GeneratePersonalizedSlipActionStep>,
    ) => void;
  }) {
    const { t } = useTranslation();
    const team = useRequiredContext(TeamContext);
    const outboundLabelsRpcClient = useRequiredContext(
      RedoOutboundLabelsRpcClientContext,
    );

    const personalizedSlipTemplates = useLoad(async (signal) => {
      try {
        const result =
          await outboundLabelsRpcClient.getPersonalizedSlipTemplatesByTeam(
            { teamId: team._id },
            { signal },
          );
        return result || [];
      } catch (error) {
        console.error("Failed to load personalized slip templates:", error);
        return [];
      }
    }, []);

    const templateOptions: RedoListItem<PersonalizedSlip>[] = useMemo(() => {
      if (
        !personalizedSlipTemplates.value ||
        personalizedSlipTemplates.value.length === 0
      ) {
        return [];
      }

      return personalizedSlipTemplates.value.map((template) => ({
        id: template._id,
        value: template,
        type: "text" as const,
        text: template.name,
      }));
    }, [personalizedSlipTemplates.value]);

    const setPersonalizedSlipTemplate = useHandler(
      ({ item }: { item: RedoListItem<PersonalizedSlip> }) => {
        setStep({
          ...step,
          personalizedSlipTemplateId: item.value._id,
          personalizedSlipTemplateName: item.value.name,
        });
      },
    );

    return (
      <RedoSingleSelectDropdownInput
        fitToAnchor={false}
        flexGrow={false}
        options={templateOptions}
        optionSelected={setPersonalizedSlipTemplate}
        placeholder={t("Select template")}
        selectedItem={templateOptions.find(
          (template) => template.value._id === step.personalizedSlipTemplateId,
        )}
        size="xs"
      />
    );
  },
);

const PersonalizedInsertOverridesInput = memo(
  function PersonalizedInsertOverridesInput({
    step,
    setStep,
  }: {
    step: PartialWithRequiredType<GeneratePersonalizedSlipActionStep>;
    setStep: (
      step: PartialWithRequiredType<GeneratePersonalizedSlipActionStep>,
    ) => void;
  }) {
    const { t } = useTranslation();
    const sizeOptions: RedoListItem<
      PageSize | typeof TEMPLATE_DEFAULT_OPTION_ID
    >[] = useMemo(
      () => [
        {
          id: TEMPLATE_DEFAULT_OPTION_ID,
          value: TEMPLATE_DEFAULT_OPTION_ID,
          type: "text" as const,
          text: t("Template default"),
        },
        ...Object.values(PageSize).map((size) => ({
          id: size,
          value: size,
          type: "text" as const,
          text: PAGE_SIZE_LABELS[size],
        })),
      ],
      [t],
    );

    const orientationOptions: RedoListItem<
      PageOrientation | typeof TEMPLATE_DEFAULT_OPTION_ID
    >[] = useMemo(
      () => [
        {
          id: TEMPLATE_DEFAULT_OPTION_ID,
          value: TEMPLATE_DEFAULT_OPTION_ID,
          type: "text" as const,
          text: t("Template default"),
        },
        ...Object.values(PageOrientation).map((orientation) => ({
          id: orientation,
          value: orientation,
          type: "text" as const,
          text: personalizedSlipOrientationLabel(orientation),
        })),
      ],
      [t],
    );

    const setSize = useHandler(
      ({
        item,
      }: {
        item: RedoListItem<PageSize | typeof TEMPLATE_DEFAULT_OPTION_ID>;
      }) => {
        setStep({
          ...step,
          pageSizeOverride:
            item.value === TEMPLATE_DEFAULT_OPTION_ID ? undefined : item.value,
        });
      },
    );

    const setOrientation = useHandler(
      ({
        item,
      }: {
        item: RedoListItem<PageOrientation | typeof TEMPLATE_DEFAULT_OPTION_ID>;
      }) => {
        setStep({
          ...step,
          pageOrientationOverride:
            item.value === TEMPLATE_DEFAULT_OPTION_ID ? undefined : item.value,
        });
      },
    );

    const selectedSize = sizeOptions.find(
      (o) => o.value === (step.pageSizeOverride ?? TEMPLATE_DEFAULT_OPTION_ID),
    );
    const selectedOrientation = orientationOptions.find(
      (o) =>
        o.value ===
        (step.pageOrientationOverride ?? TEMPLATE_DEFAULT_OPTION_ID),
    );

    return (
      <Flex dir="column" gap="xs">
        <Text fontSize="xs" fontWeight="medium">
          {t("Paper size")}
        </Text>
        <RedoSingleSelectDropdownInput
          fitToAnchor={false}
          options={sizeOptions}
          optionSelected={setSize}
          selectedItem={selectedSize}
          size="xs"
        />
        <Text fontSize="xs" fontWeight="medium">
          {t("Orientation")}
        </Text>
        <RedoSingleSelectDropdownInput
          fitToAnchor={false}
          options={orientationOptions}
          optionSelected={setOrientation}
          selectedItem={selectedOrientation}
          size="xs"
        />
        <Text fontSize="xs" textColor="secondary">
          {t(
            "Overrides the template's default page layout when this automation generates the slip.",
          )}
        </Text>
      </Flex>
    );
  },
);

const BillingAccountDropdown = memo(function BillingAccountDropdown({
  step,
  setStep,
}: {
  step: PartialWithRequiredType<SetThirdPartyBillingAccountActionStep>;
  setStep: (
    step: PartialWithRequiredType<SetThirdPartyBillingAccountActionStep>,
  ) => void;
}) {
  const { t } = useTranslation();
  const merchantRpcClient = useRequiredContext(RedoMerchantRpcClientContext);

  const billingAccounts = useLoad(
    async (signal) => {
      const result = await merchantRpcClient.getThirdPartyBillingAccounts(null);
      return result.accounts || [];
    },
    [merchantRpcClient],
  );

  const billingAccountOptions: RedoListItem<{
    _id: string;
    accountName: string;
    accountNumber: string;
    carrier: string;
  }>[] = useMemo(() => {
    if (!billingAccounts.value) {
      return [];
    }

    return billingAccounts.value.map((account) => ({
      id: account._id.toString(),
      value: account,
      type: "text" as const,
      text:
        billingAccountType(account) === CarrierBillingAccountType.Receiver
          ? t("{{accountName}} ({{carrier}}, receiver)", {
              accountName: account.accountName,
              carrier: carrierToLabel[account.carrier],
            })
          : t("{{accountName}} ({{carrier}}, third party)", {
              accountName: account.accountName,
              carrier: carrierToLabel[account.carrier],
            }),
    }));
  }, [billingAccounts.value, t]);

  const setBillingAccount = useHandler(
    ({
      item,
    }: {
      item: RedoListItem<{
        _id: string;
        accountName: string;
        accountNumber: string;
        carrier: string;
      }>;
    }) => {
      setStep({
        ...step,
        thirdPartyBillingAccountId: item.value._id,
        thirdPartyBillingAccountName: item.value.accountName,
      });
    },
  );

  return (
    <RedoSingleSelectDropdownInput
      fitToAnchor={false}
      flexGrow={false}
      options={billingAccountOptions}
      optionSelected={setBillingAccount}
      placeholder={t("Select billing account")}
      selectedItem={billingAccountOptions.find(
        (a) => a.value._id === step.thirdPartyBillingAccountId,
      )}
      size="xs"
    />
  );
});

const DeliveryMethodDropdown = memo(function DeliveryMethodDropdown({
  step,
  setStep,
}: {
  step: PartialWithRequiredType<SetDeliveryMethodActionStep>;
  setStep: (step: PartialWithRequiredType<SetDeliveryMethodActionStep>) => void;
}) {
  const { t } = useTranslation();
  const deliveryMethodOptions: RedoListItem<RedoShopifyDeliveryMethodType>[] =
    useMemo(() => {
      const availableDeliveryMethods = [
        RedoShopifyDeliveryMethodType.Shipping,
        RedoShopifyDeliveryMethodType.Local,
        RedoShopifyDeliveryMethodType.PickUp,
        RedoShopifyDeliveryMethodType.Freight,
      ];
      return availableDeliveryMethods.map((deliveryMethod) => ({
        id: deliveryMethod,
        value: deliveryMethod,
        type: "text" as const,
        text: RedoShopifyDeliveryMethodTypeToString[deliveryMethod],
      }));
    }, []);

  const setDeliveryMethod = useHandler(
    ({ item }: { item: RedoListItem<RedoShopifyDeliveryMethodType> }) => {
      setStep({ ...step, deliveryMethod: item.value });
    },
  );

  return (
    <RedoSingleSelectDropdownInput
      fitToAnchor={false}
      flexGrow={false}
      options={deliveryMethodOptions}
      optionSelected={setDeliveryMethod}
      placeholder={t("Select delivery method")}
      selectedItem={deliveryMethodOptions.find(
        (a) => a.value === step.deliveryMethod,
      )}
      size="xs"
    />
  );
});

function getOmsOptions(
  language: string,
): TextRedoListItem<OmsSettableFields>[] {
  void language;
  const omsOptions: TextRedoListItem<OmsSettableFields>[] = Object.values(
    OmsSettableFields,
  ).map((field) => ({
    id: field,
    type: "text",
    value: field,
    text: omsSettableToFriendly(field),
  }));
  omsOptions.sort((a, b) => a.text.localeCompare(b.text));
  return omsOptions;
}

function omsSettableToFriendly(field: OmsSettableFields) {
  switch (field) {
    case OmsSettableFields.SHIPPING_SERVICE:
      return t("Set shipping service");
    case OmsSettableFields.PARCEL:
      return t("Set parcel");
    case OmsSettableFields.SHIPPING_SERVICE_CHEAPEST:
      return t("Set shipping service to cheapest option");
    case OmsSettableFields.SHIPPING_SERVICE_FASTEST:
      return t("Set shipping service to fastest option");
    case OmsSettableFields.SHIPPING_SERVICE_CHEAPEST_WITHIN_DELIVERY_DAYS:
      return t("Set shipping service to cheapest within a delivery window");
    case OmsSettableFields.PACKING_SLIP_TEMPLATE:
      return t("Set packing slip template");
    case OmsSettableFields.TAG:
      return t("Add fulfillment tags");
    case OmsSettableFields.REMOVE_SHIPMENT_TAGS:
      return t("Remove fulfillment tags");
    case OmsSettableFields.SHIPPING_OPTIONS:
      return t("Set shipping options");
    case OmsSettableFields.PLACE_HOLD:
      return t("Place hold");
    case OmsSettableFields.PLACE_HOLD_UNTIL:
      return t("Place hold until");
    case OmsSettableFields.SET_WEIGHT:
      return t("Set weight");
    case OmsSettableFields.ROUND_WEIGHT:
      return t("Round weight");
    case OmsSettableFields.ASSIGNED_USER:
      return t("Set assigned user");
    case OmsSettableFields.DUTIES_PAYER:
      return t("Set duties payer");
    case OmsSettableFields.TAX_IDENTIFIERS:
      return t("Set tax identifiers");
    case OmsSettableFields.RETURN_ADDRESS:
      return t("Set return address");
    case OmsSettableFields.FULFILLMENT_SERVICE:
      return t("Assign fulfillment service");
    case OmsSettableFields.ORIGIN_LOCATION:
      return t("Set fulfillment location");
    case OmsSettableFields.BUY_RETURN_LABEL:
      return t("Set return label preference");
    case OmsSettableFields.THIRD_PARTY_BILLING_ACCOUNT:
      return t("Set billing account");
    case OmsSettableFields.DELIVERY_METHOD:
      return t("Set delivery method");
    case OmsSettableFields.OVERRIDE_USE_ORIGINAL_DESTINATION_ADDRESS:
      return t("Address verification override");
    case OmsSettableFields.PERSONALIZED_SLIP:
      return t("Generate personalized insert");
    case OmsSettableFields.INTERNAL_NOTE:
      return t("Set internal note");
    case OmsSettableFields.SPLIT_INTO_PARCELS:
      return t("Split into parcels");
    case OmsSettableFields.ORDER_TAG:
      return t("Add order tags");
    case OmsSettableFields.CUSTOMER_TAG:
      return t("Add customer tags ({{platformName}} only)", {
        platformName: "Shopify",
      });
    case OmsSettableFields.SEND_LABEL_PURCHASED_NOTIFICATION:
      return t("Send label purchased notification");
    case OmsSettableFields.DELAY_MARKETPLACE_FULFILLMENT:
      return t("Delay marketplace fulfillment");
    case OmsSettableFields.DO_NOT_NOTIFY_MARKETPLACE:
      return t("Do not notify marketplace");
    case OmsSettableFields.UPDATE_SHOPIFY_ORDER:
      return t("Update {{platformName}} order", { platformName: "Shopify" });
    default:
      assertNever(field);
  }
}

interface OmsActionsProps {
  step: PartialWithRequiredType<OmsActionStep> | null;
  stepId: string;
  nextId: string | undefined;
  setStep: (step: PartialWithRequiredType<OmsActionStep>) => void;
  schema: Schema;
  schemaType: SchemaType;
  readonly?: boolean;
}

// search-tag:ADDING-AN-OMS-ACTION
export function useOmsActions({
  schema,
  schemaType,
  step,
  setStep,
  readonly,
  stepId,
  nextId,
}: OmsActionsProps): ActionDefinition[] {
  const { t, i18n } = useTranslation();
  const omsOptions = useMemo(
    () => getOmsOptions(i18n.language),
    [i18n.language],
  );
  const setActionType = (
    actionType: OmsActionType,
    field: OmsSettableFields,
  ) => {
    setStep({
      actionType,
      field,
      type: StepType.OMS_ACTION,
      nextId,
      id: stepId,
    } as PartialWithRequiredType<OmsActionStep>);
  };
  let joinText = "";
  const setField = useHandler((field: OmsSettableFields) => {
    switch (field) {
      case OmsSettableFields.SHIPPING_SERVICE:
        setActionType(
          OmsActionType.SET_SHIPPING_SERVICE,
          OmsSettableFields.SHIPPING_SERVICE,
        );
        break;
      case OmsSettableFields.SHIPPING_SERVICE_CHEAPEST:
        setActionType(
          OmsActionType.SET_SHIPPING_SERVICE_CHEAPEST,
          OmsSettableFields.SHIPPING_SERVICE_CHEAPEST,
        );
        break;
      case OmsSettableFields.SHIPPING_SERVICE_FASTEST:
        setActionType(
          OmsActionType.SET_SHIPPING_SERVICE_FASTEST,
          OmsSettableFields.SHIPPING_SERVICE_FASTEST,
        );
        break;
      case OmsSettableFields.SHIPPING_SERVICE_CHEAPEST_WITHIN_DELIVERY_DAYS:
        setActionType(
          OmsActionType.SET_SHIPPING_SERVICE_CHEAPEST_WITHIN_DELIVERY_DAYS,
          OmsSettableFields.SHIPPING_SERVICE_CHEAPEST_WITHIN_DELIVERY_DAYS,
        );
        break;
      case OmsSettableFields.PARCEL:
        setActionType(OmsActionType.SET_PARCEL, field);
        break;
      case OmsSettableFields.PACKING_SLIP_TEMPLATE:
        setActionType(OmsActionType.SET_PACKING_SLIP_TEMPLATE, field);
        break;
      case OmsSettableFields.TAG:
        setActionType(OmsActionType.ADD_TAG, field);
        break;
      case OmsSettableFields.REMOVE_SHIPMENT_TAGS:
        setActionType(OmsActionType.REMOVE_SHIPMENT_TAGS, field);
        break;
      case OmsSettableFields.SHIPPING_OPTIONS:
        setActionType(OmsActionType.SET_SHIPPING_OPTIONS, field);
        break;
      case OmsSettableFields.PLACE_HOLD:
        setStep({
          actionType: OmsActionType.PLACE_HOLD,
          field: OmsSettableFields.PLACE_HOLD,
          type: StepType.OMS_ACTION,
          nextId,
          id: stepId,
          holdOptions: {
            holdReason: RedoShopifyFulfillmentHoldReason.Other,
            ...(step && "holdOptions" in step ? step.holdOptions : null),
            holdType: AutoHoldType.Duration,
            duration: 1,
            timeUnit: TimeUnit.DAY,
          },
        } as PartialWithRequiredType<OmsActionStep>);

        break;
      case OmsSettableFields.PLACE_HOLD_UNTIL: {
        const tomorrow = new Date();
        tomorrow.setDate(tomorrow.getDate() + 1);
        tomorrow.setHours(9, 0, 0, 0);

        setStep({
          actionType: OmsActionType.PLACE_HOLD_UNTIL,
          field: OmsSettableFields.PLACE_HOLD_UNTIL,
          type: StepType.OMS_ACTION,
          nextId,
          id: stepId,
          holdOptions: {
            holdReason: RedoShopifyFulfillmentHoldReason.Other,
            ...(step && "holdOptions" in step ? step.holdOptions : null),
            holdType: AutoHoldType.Date,
            date: tomorrow,
          },
        } as PartialWithRequiredType<OmsActionStep>);
        break;
      }
      case OmsSettableFields.SET_WEIGHT:
        setStep({
          actionType: OmsActionType.SET_WEIGHT,
          field: OmsSettableFields.SET_WEIGHT,
          type: StepType.OMS_ACTION,
          nextId,
          id: stepId,
          weight: {
            unit: WeightUnit.Oz,
            value: 16, // Default value of 1 pound (16 oz)
          },
        } as PartialWithRequiredType<OmsActionStep>);
        break;
      case OmsSettableFields.ROUND_WEIGHT:
        setStep({
          actionType: OmsActionType.ROUND_WEIGHT,
          field: OmsSettableFields.ROUND_WEIGHT,
          type: StepType.OMS_ACTION,
          nextId,
          id: stepId,
          roundingType: WeightRoundingType.ROUND_UP,
          minimumWeight: {
            unit: WeightUnit.Lb,
            value: 1, // Default minimum of 1 pound
          },
        } as PartialWithRequiredType<OmsActionStep>);
        break;
      case OmsSettableFields.ASSIGNED_USER:
        setActionType(OmsActionType.SET_ASSIGNED_USER, field);
        break;
      case OmsSettableFields.DUTIES_PAYER:
        setActionType(OmsActionType.SET_DUTIES_PAYER, field);
        break;
      case OmsSettableFields.TAX_IDENTIFIERS:
        setActionType(OmsActionType.SET_TAX_IDENTIFIERS, field);
        break;
      case OmsSettableFields.RETURN_ADDRESS:
        setActionType(OmsActionType.SET_RETURN_ADDRESS, field);
        break;
      case OmsSettableFields.FULFILLMENT_SERVICE:
        setActionType(OmsActionType.ASSIGN_FULFILLMENT_SERVICE, field);
        break;
      case OmsSettableFields.ORIGIN_LOCATION:
        setActionType(OmsActionType.SET_ORIGIN_LOCATION, field);
        break;
      case OmsSettableFields.BUY_RETURN_LABEL:
        setActionType(OmsActionType.SET_BUY_RETURN_LABEL, field);
        break;
      case OmsSettableFields.THIRD_PARTY_BILLING_ACCOUNT:
        setActionType(OmsActionType.SET_THIRD_PARTY_BILLING_ACCOUNT, field);
        break;
      case OmsSettableFields.DELIVERY_METHOD:
        setActionType(OmsActionType.SET_DELIVERY_METHOD, field);
        break;
      case OmsSettableFields.OVERRIDE_USE_ORIGINAL_DESTINATION_ADDRESS:
        setActionType(
          OmsActionType.SET_OVERRIDE_USE_ORIGINAL_DESTINATION_ADDRESS,
          field,
        );
        break;
      case OmsSettableFields.PERSONALIZED_SLIP:
        setActionType(OmsActionType.GENERATE_PERSONALIZED_SLIP, field);
        break;
      case OmsSettableFields.INTERNAL_NOTE:
        setActionType(OmsActionType.SET_INTERNAL_NOTE, field);
        break;
      case OmsSettableFields.SPLIT_INTO_PARCELS:
        setActionType(OmsActionType.SPLIT_INTO_PARCELS, field);
        break;
      case OmsSettableFields.ORDER_TAG:
        setActionType(OmsActionType.ADD_ORDER_TAGS, field);
        break;
      case OmsSettableFields.CUSTOMER_TAG:
        setActionType(OmsActionType.ADD_CUSTOMER_TAGS, field);
        break;
      case OmsSettableFields.SEND_LABEL_PURCHASED_NOTIFICATION:
        setActionType(
          OmsActionType.SET_SEND_LABEL_PURCHASED_NOTIFICATION,
          field,
        );
        break;
      case OmsSettableFields.DELAY_MARKETPLACE_FULFILLMENT:
        setStep({
          actionType: OmsActionType.DELAY_MARKETPLACE_FULFILLMENT,
          field: OmsSettableFields.DELAY_MARKETPLACE_FULFILLMENT,
          type: StepType.OMS_ACTION,
          nextId,
          id: stepId,
          delayDays: 1,
        });
        break;
      case OmsSettableFields.DO_NOT_NOTIFY_MARKETPLACE:
        setActionType(OmsActionType.DO_NOT_NOTIFY_MARKETPLACE, field);
        break;
      case OmsSettableFields.UPDATE_SHOPIFY_ORDER:
        setStep({
          actionType: OmsActionType.UPDATE_SHOPIFY_ORDER,
          field: OmsSettableFields.UPDATE_SHOPIFY_ORDER,
          type: StepType.OMS_ACTION,
          nextId,
          id: stepId,
          updateField: UpdateShopifyOrderField.SHIPPING_LABEL_COST,
        } as PartialWithRequiredType<OmsActionStep>);
        break;
      default:
        assertNever(field);
    }
  });

  let row1Fields: React.ReactNode = null;
  let row2Fields: React.ReactNode = null;
  if (step) {
    switch (step.field) {
      case undefined:
        break;
      case OmsSettableFields.SHIPPING_SERVICE: {
        joinText = t("to");
        row2Fields = <OmsShippingServiceInputs setStep={setStep} step={step} />;

        break;
      }
      case OmsSettableFields.SHIPPING_SERVICE_CHEAPEST: {
        joinText = t("of");
        row2Fields = (
          <OmsShippingServiceGroupInputs
            selectionCriteria={SortingCriteria.CHEAPEST}
            setStep={setStep}
            step={step}
          />
        );

        break;
      }
      case OmsSettableFields.SHIPPING_SERVICE_FASTEST: {
        joinText = t("of");
        row2Fields = (
          <OmsShippingServiceGroupInputs
            selectionCriteria={SortingCriteria.FASTEST}
            setStep={setStep}
            step={step}
          />
        );

        break;
      }
      case OmsSettableFields.SHIPPING_SERVICE_CHEAPEST_WITHIN_DELIVERY_DAYS: {
        joinText = t("of");
        const selectionCriteria = getDeliveryWindowSortingCriteria(step);
        row2Fields = (
          <OmsShippingServiceGroupInputs
            selectionCriteria={selectionCriteria}
            setStep={setStep}
            step={step}
          />
        );

        break;
      }
      case OmsSettableFields.PARCEL: {
        row2Fields = <ParcelInput setStep={setStep} step={step} />;

        break;
      }
      case OmsSettableFields.PACKING_SLIP_TEMPLATE: {
        joinText = t("to");
        row1Fields = <PackingSlipTemplateInput setStep={setStep} step={step} />;

        break;
      }
      case OmsSettableFields.TAG: {
        row2Fields = <TagInput setStep={setStep} step={step} />;

        break;
      }
      case OmsSettableFields.REMOVE_SHIPMENT_TAGS:
        row2Fields = (
          <TagInput allowCreatingTags={false} setStep={setStep} step={step} />
        );
        break;
      case OmsSettableFields.SHIPPING_OPTIONS: {
        row2Fields = <ShippingOptionsInput setStep={setStep} step={step} />;

        break;
      }
      case OmsSettableFields.PLACE_HOLD: {
        joinText = t("for");
        row1Fields = <HoldDaysInput setStep={setStep} step={step} />;
        row2Fields = <HoldDetailsInput setStep={setStep} step={step} />;

        break;
      }
      case OmsSettableFields.PLACE_HOLD_UNTIL: {
        row1Fields = <HoldDateInput setStep={setStep} step={step} />;
        row2Fields = <HoldDetailsInput setStep={setStep} step={step} />;

        break;
      }
      case OmsSettableFields.SET_WEIGHT: {
        joinText = t("to");
        row2Fields = <WeightInput setStep={setStep} step={step} />;

        break;
      }
      case OmsSettableFields.ROUND_WEIGHT: {
        joinText = "";
        row1Fields = <RoundWeightInput setStep={setStep} step={step} />;

        break;
      }
      case OmsSettableFields.ASSIGNED_USER: {
        row2Fields = <UserDropdownInput setStep={setStep} step={step} />;

        break;
      }
      case OmsSettableFields.DUTIES_PAYER: {
        joinText = t("to");
        row2Fields = <DutiesPayerRadioGroup setStep={setStep} step={step} />;

        break;
      }
      case OmsSettableFields.TAX_IDENTIFIERS: {
        joinText = t("to");
        row2Fields = <TaxIdentifiersDropdown setStep={setStep} step={step} />;

        break;
      }
      case OmsSettableFields.RETURN_ADDRESS: {
        joinText = t("to");
        row2Fields = <ReturnAddressDropdown setStep={setStep} step={step} />;

        break;
      }
      case OmsSettableFields.FULFILLMENT_SERVICE: {
        joinText = t("to");
        row2Fields = (
          <FulfillmentServiceDropdown setStep={setStep} step={step} />
        );

        break;
      }
      case OmsSettableFields.ORIGIN_LOCATION: {
        joinText = t("to");
        row2Fields = <OriginLocationDropdown setStep={setStep} step={step} />;
        break;
      }
      case OmsSettableFields.BUY_RETURN_LABEL: {
        joinText = t("to");
        row2Fields = <BuyReturnLabelToggle setStep={setStep} step={step} />;
        break;
      }
      case OmsSettableFields.THIRD_PARTY_BILLING_ACCOUNT: {
        joinText = t("to");
        row2Fields = <BillingAccountDropdown setStep={setStep} step={step} />;
        break;
      }
      case OmsSettableFields.DELIVERY_METHOD: {
        joinText = t("to");
        row2Fields = (
          <DeliveryMethodDropdown
            setStep={
              setStep as (
                step: PartialWithRequiredType<SetDeliveryMethodActionStep>,
              ) => void
            }
            step={step as PartialWithRequiredType<SetDeliveryMethodActionStep>}
          />
        );
        break;
      }
      case OmsSettableFields.OVERRIDE_USE_ORIGINAL_DESTINATION_ADDRESS: {
        row2Fields = (
          <OverrideUseOriginalDestinationAddressToggle
            setStep={setStep}
            step={step}
          />
        );
        break;
      }
      case OmsSettableFields.PERSONALIZED_SLIP: {
        joinText = t("using");
        row1Fields = (
          <PersonalizedInsertTemplateInput
            setStep={
              setStep as (
                step: PartialWithRequiredType<GeneratePersonalizedSlipActionStep>,
              ) => void
            }
            step={
              step as PartialWithRequiredType<GeneratePersonalizedSlipActionStep>
            }
          />
        );
        row2Fields = (
          <PersonalizedInsertOverridesInput
            setStep={
              setStep as (
                step: PartialWithRequiredType<GeneratePersonalizedSlipActionStep>,
              ) => void
            }
            step={
              step as PartialWithRequiredType<GeneratePersonalizedSlipActionStep>
            }
          />
        );
        break;
      }
      case OmsSettableFields.INTERNAL_NOTE: {
        joinText = t("to");
        row2Fields = (
          <>
            <RedoTextInput
              placeholder={t("Enter internal note")}
              setValue={(value) => setStep({ ...step, note: value })}
              value={(step as { note?: string }).note || ""}
            />
            <Text fontSize="xs" textColor="secondary">
              {t(
                "This will not override existing internal notes if automations are run again. If no internal note exists, this will be set.",
              )}
            </Text>
          </>
        );
        break;
      }
      case OmsSettableFields.SPLIT_INTO_PARCELS: {
        row2Fields = (
          <Text fontSize="xs" textColor="secondary">
            {t(
              'Items marked with "own package" will each be placed in their own parcel.',
            )}
          </Text>
        );
        break;
      }
      case OmsSettableFields.ORDER_TAG: {
        row2Fields = <OrderTagInput setStep={setStep} step={step} />;
        break;
      }
      case OmsSettableFields.CUSTOMER_TAG: {
        row2Fields = <CustomerTagInput setStep={setStep} step={step} />;
        break;
      }
      case OmsSettableFields.SEND_LABEL_PURCHASED_NOTIFICATION: {
        row2Fields = (
          <SendLabelPurchasedNotificationToggle setStep={setStep} step={step} />
        );
        break;
      }
      case OmsSettableFields.DELAY_MARKETPLACE_FULFILLMENT: {
        joinText = t("by");
        row1Fields = (
          <DelayMarketplaceFulfillmentInput
            setStep={setStep}
            step={
              step as PartialWithRequiredType<DelayMarketplaceFulfillmentActionStep>
            }
          />
        );
        break;
      }
      case OmsSettableFields.UPDATE_SHOPIFY_ORDER: {
        row2Fields = (
          <UpdateShopifyOrderInput
            setStep={
              setStep as (
                step: PartialWithRequiredType<UpdateShopifyOrderActionStep>,
              ) => void
            }
            step={step as PartialWithRequiredType<UpdateShopifyOrderActionStep>}
          />
        );
        break;
      }
      case OmsSettableFields.DO_NOT_NOTIFY_MARKETPLACE:
        break;
      // No default
    }
  }

  return omsOptions
    .filter(
      (option) =>
        schemaType === SchemaType.OMS_FULFILLMENT_LABEL_PURCHASED ||
        option.value !== OmsSettableFields.UPDATE_SHOPIFY_ORDER,
    )
    .map((option) => ({
      option,
      selected: step?.field === option.value,
      onSelect: () => setField(option.value),
      inlineFields: row1Fields,
      fields: row2Fields,
      joinText,
    }));
}

const OmsActionInspector = memo(function OmsActionInspector(
  props: StepTypeDetailsProps<PartialWithRequiredType<OmsActionStep>>,
) {
  const actions = useOmsActions({
    ...props,
    stepId: props.step.id,
    nextId: props.step.nextId,
  });
  return <ActionEditor actions={actions} onClose={props.onClose} />;
});

export const omsActionStepType: FrontendStepType<
  PartialWithRequiredType<OmsActionStep>
> = {
  valid: (step) => omsActionStepSchema.safeParse(step).success,
  type: "custom",
  customInspector: OmsActionInspector,
};
